import { QueryFunctionContext } from '@tanstack/react-query';

import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods, QuotesRFQApi } from '@setvi/shared/enums';
import { prepareReqBody } from 'pages/sales/modules/orders/utils/prepare-body-req';

import { QuoteFilters, QuotesAnalyticsResponse, QuotesStatus } from './Types';

export enum QuotesQueryKey {
  GET_ALL = 'GET_ALL_QUOTES',
  GET_SINGLE = 'GET_SINGLE_QUOTE',
  GET_LIST_ANALYTICS = 'GET_LIST_ANALYTICS_QUOTES',
  PREVIEW_PDF = 'PREVIEW_QUOTE_PDF',
  GET_QUOTE_CONFIGURATIONS = 'GET_QUOTE_CONFIGURATIONS',
  GET_QUOTE_STATUSES = 'GET_QUOTE_STATUSES'
}

/**
 * Query for fetching all quotes with infinite scroll pagination
 * Following Auto-RFQ pattern exactly
 */
export const getQuotesQuery = (params: QuoteFilters) => ({
  queryKey: [QuotesQueryKey.GET_ALL, params],
  queryFn: async (data: QueryFunctionContext) => {
    const offset = data.pageParam || 0;
    const limit = params.limit || 20;

    // Prepare request body using existing utility
    const body = prepareReqBody(
      {
        statusId: params.statusId,
        date: params.date,
        searchStr: params.searchStr,
        accountName: params.accountName,
        accountId: params?.accountId,
        type: params.type
      },
      offset,
      limit,
      true,
      null
    );

    const response = await axiosHelper({
      endpoint: '/quote/search',
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    });

    return response.data;
  },
  getNextPageParam: (lastPage: any, allPages: any) => {
    const limit = params.limit || 20;
    const allQuotes = allPages.reduce(
      (acc: any, curr: any) => acc + (curr?.length || 0),
      0
    );

    // If last page has less items than limit, we've reached the end
    return lastPage?.length < limit ? undefined : allQuotes;
  },
  cacheTime: 0, // Don't cache to ensure fresh data
  refetchOnWindowFocus: false
});

/**
 * Query for fetching a single quote by ID
 */
export const getQuoteQuery = (id: string | number) => ({
  queryKey: [QuotesQueryKey.GET_SINGLE, id],
  queryFn: () =>
    axiosHelper({
      endpoint: `/quote/${id}`,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

/**
 * Query for fetching quotes list analytics (metrics for cards)
 * TODO: Missing API
 */
export const getQuotesAnalyticsQuery = (params: {
  statusId?: string;
  date?: string[];
  accountName?: string;
}) => ({
  queryKey: [QuotesQueryKey.GET_LIST_ANALYTICS, params],
  queryFn: async (): Promise<QuotesAnalyticsResponse> => {
    // For now, we'll fetch quotes and calculate analytics on the frontend
    // In the future, this should be a dedicated backend endpoint
    const response = await axiosHelper({
      endpoint: '/quote/search',
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body: prepareReqBody(
        {
          statusId: params.statusId,
          date: params.date,
          accountName: params.accountName
        },
        0,
        1000, // Get more quotes for accurate analytics
        true,
        null
      )
    });

    const quotes = response.data || [];

    // Calculate analytics
    const totalQuotes = quotes.length;
    const totalQuoteValue = quotes.reduce(
      (sum: number, quote: any) => sum + (quote.totalSell || 0),
      0
    );
    const totalGP = quotes.reduce((sum: number, quote: any) => {
      const gp = (quote.totalSell || 0) - (quote.totalCost || 0);
      return sum + gp;
    }, 0);
    const averageGPPercentage =
      totalQuoteValue > 0 ? (totalGP / totalQuoteValue) * 100 : 0;

    return {
      totalQuotes,
      totalQuoteValue,
      totalGP,
      averageGPPercentage
    };
  },
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000 // Cache for 5 minutes
});

/**
 * Query for fetching quote PDF preview
 */
export const getQuotePreviewQuery = (quoteId: string | number) => ({
  queryKey: [QuotesQueryKey.PREVIEW_PDF, quoteId],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesRFQApi.PreviewPDF.replace(
        ':quoteId',
        quoteId?.toString()
      ),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

export const getQuoteConfigurationsQuery = () => ({
  queryKey: [QuotesQueryKey.GET_QUOTE_CONFIGURATIONS],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesRFQApi.Configurations,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

export const getQuoteStatusesQuery = () => ({
  queryKey: [QuotesQueryKey.GET_QUOTE_STATUSES],
  queryFn: () =>
    axiosHelper({
      endpoint: QuotesRFQApi.GetStatuses,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    }),
  select: (res: QuotesStatus[]) => res.sort((a, b) => a.order - b.order)
});
